<script lang="ts" setup>

import { useCartStore } from '@/stores/cart/useCartStore';
import { computed } from '#imports';

type Props = {
    displayImageURL: AppWTFNft['displayImageURL'];
    price?: string;
    name: AppWTFNft['name'];
    withoutClose?: boolean;
    itemType: AppWTFNft['itemType'];
    uuid: AppWTFNft['uuid'];
    amount?: number;
    maxAmount?: number;
    isError?: boolean;
};

const emit = defineEmits(['onRemove']);

const props = defineProps<Props>();

const { utils: { isEvmErc1155 } } = useProviderConfig()(['utils']);

const isERC1155 = computed(() => props.amount && isEvmErc1155(props.itemType));

const { changeCartItem } = useCartStore();

</script>

<template>
  <div v-bind="$attrs"
       class="cart-item flex-row display-flex justify-center align-items-center"
  >

    <div class="cart-item-content display-flex">
      <div class="cart-item-img relative">
        <ImgMain :src="displayImageURL"
                 :alt="name"
                 mode="light-mode"
        />
        <IconMain v-if="isError"
                  icon="caution"
                  class="cart-item-img__error"
                  :size="36"
        />
      </div>
      <div class="display-flex flex-column cart-item-content-info justify-center">
        <div class="display-flex flex-row align-items-center">
          <span class="cart-item__name compact-500-accent">{{ name }}</span>

          <span class="cart-item__price compact-500-numeric">{{ price }}</span>
        </div>
        <Counter v-if="isERC1155"
                 class="cart-item-content-info__counter"
                 :initial-value="props.amount!"
                 :max="maxAmount"
                 @on-change="(val: number) => changeCartItem(uuid, { amount: val })"
        />
      </div>
    </div>
    <div v-if="!withoutClose"
         class="cart-item__close cursor-pointer display-flex justify-center align-items-center"
         type="button"
         role="button"
         aria-label="close"
         @click="emit('onRemove')"
    >
      <IconMain icon="close"
                :size="20"
      />
    </div>
  </div>
</template>

<style lang="scss">

.cart-item {
  display: flex;

  &-content {
    width: calc(100% - 36px);

    &-info {
      width: calc(100% - 42px);
      margin-left: 12px;

      &__counter {
        margin-top: 8px;
      }
    }
  }

  &__close {
    width: 36px;
    min-width: 36px;
    height: 36px;
  }

  &-img {
    position: relative;
    width: 36px;
    min-width: 36px;
    height: 36px;

    &__error {
      position: absolute;
      top: 0;
      left: 0;
      width: 36px;
      height: 36px;
      opacity: 0.5;
      fill: var(--content-negative);
    }
  }

  &__name {
    flex-grow: 1;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__price {
    margin-left: $spacing-150;
    color: var(--content-secondary);
    white-space: nowrap;
  }
}

</style>
